import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import {Zone} from '../../zone/zone';
import {Client} from '../client';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {Adres} from '../adres';
import {DienstenPipe} from '../../dienst/diensten.pipe';
import {Diensten} from '../../dienst/diensten';
import {ZahValidators} from '../../core/form/validators';

@Component({
    selector: 'zah-upsert-client',
    templateUrl: './upsert-client.component.html',
    styleUrls: ['./upsert-client.component.scss'],
    standalone: false
})
export class UpsertClientComponent implements OnInit, OnChanges {
  private dienstenPipe = inject(DienstenPipe);

  @Input()
  client?: Client;
  @Input()
  zones: Array<Zone>;
  @Input()
  diensten: Diensten;
  @Input()
  okLabel: string;
  @Output()
  meldingenClicked: EventEmitter<Client> = new EventEmitter<Client>();
  @Output()
  prestatiesClicked: EventEmitter<Client> = new EventEmitter<Client>();
  @Output()
  archiveClicked: EventEmitter<Client> = new EventEmitter<Client>();
  @Output()
  unarchiveClicked: EventEmitter<Client> = new EventEmitter<Client>();
  @Output()
  editDienstenClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  cancelClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  okClicked: EventEmitter<Client> = new EventEmitter<Client>();

  upsertClientForm: FormGroup;

  ngOnInit(): void {
    this.upsertClientForm = new FormGroup({
      voornaam: new FormControl(this.client?.getVoornaam(), ZahValidators.notBlank),
      achternaam: new FormControl(this.client?.getAchternaam(), ZahValidators.notBlank),
      clientNummer: new FormControl(this.client?.getNummer(), ZahValidators.notBlank),
      straat: new FormControl(this.client?.getAdres().getStraat(), ZahValidators.notBlank),
      straatNummer: new FormControl(this.client?.getAdres().getNummer(), ZahValidators.notBlank),
      bus: new FormControl(this.client?.getAdres().getBus()),
      postcode: new FormControl(this.client?.getAdres().getPostcode(), ZahValidators.notBlank),
      gemeente: new FormControl(this.client?.getAdres().getGemeente(), ZahValidators.notBlank),
      diensten: new FormControl({
        value: this.dienstenPipe.transform(this.client?.getDienstIds(), this.diensten),
        disabled: true,
      }),
    });
    this.setFormState(this.client);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.client && !changes.client.isFirstChange()) {
      this.setFormState(changes.client.currentValue as Client);
    }
  }

  private setFormState(client: Client): void {
    if (client?.isGearchiveerd()) {
      this.upsertClientForm.disable();
    } else {
      this.upsertClientForm.enable();
    }
  }

  onMeldingenClicked(client: Client): void {
    this.meldingenClicked.emit(client);
  }

  onPrestatiesClick(client: Client): void {
    this.prestatiesClicked.emit(client);
  }

  onArchiveClick(client: Client): void {
    this.archiveClicked.emit(client);
  }

  onUnarchiveClick(client: Client): void {
    this.unarchiveClicked.emit(client);
  }

  onEditDienstenClick(): void {
    this.editDienstenClicked.emit();
  }

  onCancelClicked(): void {
    this.cancelClicked.emit();
  }

  onOkClicked(): void {
    const client = new Client(
      this.client?.getId(),
      this.voornaam.value,
      this.achternaam.value,
      this.clientNummer.value,
      new Adres(
        this.straat.value,
        this.straatNummer.value,
        this.bus.value,
        this.postcode.value,
        this.gemeente.value,
      ),
      this.client ? this.client.getDiensten() : [],
      undefined,
      this.client ? this.client.getFiche() : undefined,
    );
    this.okClicked.emit(client);
  }

  get voornaam(): AbstractControl {
    return this.upsertClientForm.controls.voornaam;
  }

  get achternaam(): AbstractControl {
    return this.upsertClientForm.controls.achternaam;
  }

  get clientNummer(): AbstractControl {
    return this.upsertClientForm.controls.clientNummer;
  }

  get zone(): AbstractControl {
    return this.upsertClientForm.controls.zone;
  }

  get straat(): AbstractControl {
    return this.upsertClientForm.controls.straat;
  }

  get straatNummer(): AbstractControl {
    return this.upsertClientForm.controls.straatNummer;
  }

  get bus(): AbstractControl {
    return this.upsertClientForm.controls.bus;
  }

  get postcode(): AbstractControl {
    return this.upsertClientForm.controls.postcode;
  }

  get gemeente(): AbstractControl {
    return this.upsertClientForm.controls.gemeente;
  }
}
